// ============================================================================
// Constants — eFakt Frontend
// ============================================================================

import type { KSeFEnvironment, KSeFRegistrationStatus, UserRole } from "./types";

// ---------------------------------------------------------------------------
// API Configuration
// ---------------------------------------------------------------------------

/** Backend API base URLs per deployment target */
export const API_ENDPOINTS = {
  /** Local development — Next.js rewrite proxy */
  LOCAL: "/api/backend",
  /** Test server — Nginx proxy (strips /api/ prefix) */
  TEST: "http://ef-test.robak.net.pl/api",
  /** Production — ef.softwork.pl */
  PRODUCTION: "https://ef.softwork.pl/api",
} as const;

export type ApiEndpoint = keyof typeof API_ENDPOINTS;

// ---------------------------------------------------------------------------
// KSeF Environments
// ---------------------------------------------------------------------------

/** Available KSeF 2.0 environments */
export const KSEF_ENVIRONMENTS: Record<KSeFEnvironment, { label: string; url: string }> = {
  TEST: {
    label: "Test (api-test.ksef.mf.gov.pl)",
    url: "https://api-test.ksef.mf.gov.pl",
  },
  DEMO: {
    label: "Demo (api-demo.ksef.mf.gov.pl)",
    url: "https://api-demo.ksef.mf.gov.pl",
  },
  PRD: {
    label: "Production (api.ksef.mf.gov.pl)",
    url: "https://api.ksef.mf.gov.pl",
  },
};

export const KSEF_ENVIRONMENT_OPTIONS: KSeFEnvironment[] = ["TEST", "DEMO", "PRD"];

// ---------------------------------------------------------------------------
// User Roles
// ---------------------------------------------------------------------------

export const USER_ROLES: UserRole[] = ["super-admin", "admin", "user"];

export const ROLE_LABELS: Record<UserRole, string> = {
  "super-admin": "Super Admin",
  admin: "Admin",
  user: "User",
};

/** Roles that have admin-level access */
export const ADMIN_ROLES: UserRole[] = ["super-admin", "admin"];

// ---------------------------------------------------------------------------
// KSeF Registration Statuses
// ---------------------------------------------------------------------------

export const KSEF_REGISTRATION_STATUSES: KSeFRegistrationStatus[] = [
  "Pending",
  "Confirmed",
  "Failed",
  "TimedOut",
];

export const REGISTRATION_STATUS_LABELS: Record<KSeFRegistrationStatus, string> = {
  Pending: "Pending",
  Confirmed: "Confirmed",
  Failed: "Failed",
  TimedOut: "Timed Out",
};

// ---------------------------------------------------------------------------
// Received Document Processing Statuses
// ---------------------------------------------------------------------------

export const DOCUMENT_STATUSES = {
  NEW: "new",
  REVIEWED: "reviewed",
  EXPORTED: "exported",
  REJECTED: "rejected",
} as const;

export type DocumentStatus = (typeof DOCUMENT_STATUSES)[keyof typeof DOCUMENT_STATUSES];

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  new: "New",
  reviewed: "Reviewed",
  exported: "Exported",
  rejected: "Rejected",
};

// ---------------------------------------------------------------------------
// AppSetting Keys
// ---------------------------------------------------------------------------

/** Well-known setting keys stored in the AppSetting table */
export const APP_SETTING_KEYS = {
  KSEF_ENVIRONMENT: "ksef-environment",
  COMPANY_NIP: "company-nip",
  COMPANY_PESEL: "company-pesel",
  COMPANY_NAME: "company-name",
  DOCUMENT_PULL_ENABLED: "document-pull-enabled",
  DOCUMENT_PULL_INTERVAL: "document-pull-interval-minutes",
  EXTERNAL_SYSTEM_ENABLED: "external-system-enabled",
  EXTERNAL_SYSTEM_URL: "external-system-url",
  SESSION_LOOP_TEST_ENABLED: "session-loop-test-enabled",
  SESSION_LOOP_TEST_CYCLE: "session-loop-test-cycle-seconds",
  SESSION_LOOP_PROD_ENABLED: "session-loop-prod-enabled",
  SESSION_LOOP_PROD_CYCLE: "session-loop-prod-cycle-seconds",
} as const;

export type AppSettingKey = (typeof APP_SETTING_KEYS)[keyof typeof APP_SETTING_KEYS];

// ---------------------------------------------------------------------------
// Pagination Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
